import { FC, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useLazyGetApiUploadedFilesQuery } from "src/app/services/api";
import { useNavigate } from "react-router-dom";
import momentnpm from "jalali-moment";
import moment from "moment";

interface FileData {
  id: string;
  name: string;
  size: string;
  status: "تکمیل شده" | "ناتمام";
  uploadDate: string;
}

const Dashboard: FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [fetchFiles, { data, error, isLoading, isUninitialized }] = useLazyGetApiUploadedFilesQuery();

  useEffect(() => {
    if (isUninitialized) {
      fetchFiles();
    }
  }, [fetchFiles, isUninitialized]);

  const getStatusText = (status: number | string): "تکمیل شده" | "ناتمام" => {
    if (typeof status === 'number') {
      switch (status) {
        case 1: return "ناتمام";
        case 2: return "تکمیل شده";
        default: return "ناتمام";
      }
    } else {
      switch (status) {
        case "2": return "تکمیل شده";
        case "1": return "ناتمام";
        case "completed": return "تکمیل شده";
        case "processing": return "ناتمام";
        default: return "ناتمام";
      }
    }
  };

  const getStatusColor = (status: "تکمیل شده" | "ناتمام") => {
    switch (status) {
      case "تکمیل شده":
        return "success.main";
      case "ناتمام":
        return "warning.main";
      default:
        return "text.primary";
    }
  };

  // تابع برای تبدیل تاریخ میلادی به شمسی
  const convertToJalaali = (dateString: string | null): string => {
    if (!dateString) return "------------------";
    try {
      const date = moment(dateString);
      if (date.isValid()) {
        return date.format("YYYY/MM/DD - HH:mm");
      }
      return "------------------";
    } catch (error) {
      return "------------------";
    }
  };

  const extractFileName = (fileMetadata: string): string => {
    try {
      const metadata = JSON.parse(fileMetadata);
      return metadata.filename || metadata.name || "نامعلوم";
    } catch (error) {
      return fileMetadata || "نامعلوم";
    }
  };

  const convertToMB = (size: string): string => {
    try {
      if (!isNaN(Number(size))) {
        const sizeInMB = Number(size) / (1024 * 1024);
        return `${sizeInMB.toFixed(2)} MB`;
      }

      if (typeof size === 'string') {
        const sizeLower = size.toLowerCase();

        if (sizeLower.includes('kb')) {
          const kbValue = parseFloat(sizeLower.replace('kb', '').trim());
          return `${(kbValue / 1024).toFixed(2)} MB`;
        }
        else if (sizeLower.includes('mb')) {
          return size;
        }
        else if (sizeLower.includes('gb')) {
          const gbValue = parseFloat(sizeLower.replace('gb', '').trim());
          return `${(gbValue * 1024).toFixed(2)} MB`;
        }
        else if (sizeLower.includes('bytes')) {
          const bytesValue = parseFloat(sizeLower.replace('bytes', '').trim());
          return `${(bytesValue / (1024 * 1024)).toFixed(2)} MB`;
        }
        else {
          const numericValue = parseFloat(size);
          if (!isNaN(numericValue)) {
            return `${(numericValue / (1024 * 1024)).toFixed(2)} MB`;
          }
        }
      }

      return "نامعلوم";
    } catch (error) {
      return "نامعلوم";
    }
  };

  const files: FileData[] = data
    ? [...data]
      .reverse()
      .slice(0, 10)
      .map((file: any, index: number) => ({
        id: file._id || `file-${index}`,
        name: extractFileName(file.file_metadata),
        size: convertToMB(file.file_size),
        status: getStatusText(file.file_status),
        uploadDate: convertToJalaali(file.createdAt || file.updatedAt)
      }))
    : [];

  const handleViewAllFiles = () => {
    navigate("/files/uploaded-files");
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        sx={{ px: { xs: 2, md: '20%' } }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        mt: 3,
        px: { xs: 2, md: '20%' },
        boxSizing: 'border-box'
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
        <Typography variant="h6" sx={{ fontWeight: "bold", textAlign: { xs: 'center', md: 'left' } }}>
          فایل‌های آپلود شده
        </Typography>
        <Box display="flex" gap={1} flexDirection={{ xs: 'column', sm: 'row' }} width={{ xs: '100%', md: 'auto' }}>
          <Button
            variant="outlined"
            onClick={() => fetchFiles()}
            disabled={isLoading}
            fullWidth={isMobile}
            size={isMobile ? "large" : "medium"}
          >
            بروزرسانی
          </Button>
          <Button
            variant="contained"
            onClick={handleViewAllFiles}
            fullWidth={isMobile}
            size={isMobile ? "large" : "medium"}
          >
            مشاهده همه فایل‌ها
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          خطا در دریافت اطلاعات از سرور
        </Alert>
      )}

      {!error && files.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          هیچ فایلی برای نمایش وجود ندارد
        </Alert>
      )}

      <TableContainer
        component={Paper}
        elevation={3}
        sx={{
          overflowX: 'auto',
          '& .MuiTableCell-root': {
            py: 1.5,
            px: { xs: 1, md: 2 },
            fontSize: { xs: '0.75rem', md: '0.875rem' }
          }
        }}
      >
        <Table sx={{ minWidth: 400 }} aria-label="جدول فایل‌ها" size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow sx={{ backgroundColor: grey[100] }}>
              <TableCell sx={{ fontWeight: "bold", minWidth: 120 }}>نام فایل</TableCell>
              <TableCell sx={{ fontWeight: "bold", minWidth: 80 }} align="center">حجم فایل</TableCell>
              <TableCell sx={{ fontWeight: "bold", minWidth: 100 }} align="center">وضعیت</TableCell>
              <TableCell sx={{ fontWeight: "bold", minWidth: 120 }} align="center">تاریخ آپلود</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file, index) => (
              <TableRow
                key={file.id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  borderBottom: index < files.length - 1 ? `1px solid ${grey[300]}` : "none",
                  height: "60px"
                }}
              >
                <TableCell component="th" scope="row" sx={{ maxWidth: { xs: 120, md: 200 } }}>
                  <Typography
                    noWrap
                    title={file.name}
                    sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                  >
                    {file.name}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                  {file.size}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    color: getStatusColor(file.status),
                    fontWeight: "medium",
                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                  }}
                >
                  {file.status}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    direction:"ltr",
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    color: file.uploadDate === "------------------" ? grey[500] : "inherit"
                  }}
                >
                  {file.uploadDate.split('-')[1]}
                  {" "}
                  {new Date(file.uploadDate.split('-')[0].trim()).toLocaleDateString('fa-IR')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Dashboard;